import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { 
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  User,
  Phone,
  Mail,
  Shield,
  ArrowLeft,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Requirement keys that have a dedicated upload field on this form.
const FIELD_REQ_KEYS = ['cdl', 'medical_cert', 'w9', 'vehicle_insurance'];

// Derive the role-aware upload/validation view from the authoritative requirement engine
// (tokenData.requirements). Falls back to the legacy fixed set only if requirements are absent.
const getReqView = (tokenData) => {
  const reqs = (tokenData && tokenData.requirements && tokenData.requirements.requirements) || [];
  const byKey = Object.fromEntries(reqs.map((r) => [r.key, r]));
  const hasReqs = reqs.length > 0;
  const st = (k) => byKey[k] && byKey[k].status;
  return {
    hasReqs,
    reqs,
    showCdl: hasReqs ? st('cdl') === 'REQUIRED' : true,
    showMedical: hasReqs ? st('medical_cert') === 'REQUIRED' : true,
    showW9: hasReqs ? st('w9') === 'REQUIRED' : true,
    showInsurance: hasReqs
      ? ['REQUIRED', 'CONDITIONAL'].includes(st('vehicle_insurance'))
      : !!(tokenData && tokenData.requires_insurance),
    otherRequired: hasReqs
      ? reqs.filter((r) => r.status === 'REQUIRED' && !FIELD_REQ_KEYS.includes(r.key))
      : [],
  };
};

const REQ_TONE = {
  REQUIRED: 'bg-blue-50 text-blue-700 border-blue-200',
  CONDITIONAL: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  VERIFICATION_REQUIRED: 'bg-amber-50 text-amber-700 border-amber-200',
  NOT_APPLICABLE: 'bg-gray-50 text-gray-400 border-gray-200',
  NOT_REQUIRED: 'bg-gray-50 text-gray-400 border-gray-200',
};
const REQ_LABEL = {
  REQUIRED: 'Required',
  CONDITIONAL: 'Conditional',
  VERIFICATION_REQUIRED: 'Verify',
  NOT_APPLICABLE: 'N/A',
  NOT_REQUIRED: 'Not required',
};


const DriverOnboarding = () => {
  const { token } = useParams();
  const [validating, setValidating] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [files, setFiles] = useState({
    cdl: null,
    medical_card: null,
    w9: null,
    insurance: null,
    other_doc: null
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    setValidating(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/onboarding/token/${token}/validate`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid or expired link');
      }
      
      setTokenData(data);
      setFormData({
        name: `${data.first_name} ${data.last_name}`.trim(),
        email: data.email || '',
        phone: data.phone || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setValidating(false);
    }
  };

  const handleFileChange = (field, file) => {
    setFiles(prev => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      setError('Please fill in your name and email.');
      return;
    }

    const v = getReqView(tokenData);
    if (v.showCdl && !files.cdl) { setError('Please upload your CDL.'); return; }
    if (v.showMedical && !files.medical_card) { setError('Please upload your Medical Card.'); return; }
    if (v.showW9 && !files.w9) { setError('Please upload your W-9.'); return; }
    if (v.showInsurance && !files.insurance) { setError('Please upload your Certificate of Insurance.'); return; }
    for (const r of v.otherRequired) {
      if (!files[r.key]) { setError(`Please upload: ${r.label}.`); return; }
    }

    setSubmitting(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      
      if (files.cdl) formDataToSend.append('cdl', files.cdl);
      if (files.medical_card) formDataToSend.append('medical_card', files.medical_card);
      if (files.w9) formDataToSend.append('w9', files.w9);
      if (files.insurance) formDataToSend.append('insurance', files.insurance);
      if (files.other_doc) formDataToSend.append('other_doc', files.other_doc);

      // Role-aware additional required documents, one slot per authoritative requirement key.
      const vSubmit = getReqView(tokenData);
      const addKeys = [];
      vSubmit.otherRequired.forEach((r) => {
        if (files[r.key]) {
          formDataToSend.append('additional_docs', files[r.key]);
          addKeys.push(r.key);
        }
      });
      if (addKeys.length) formDataToSend.append('additional_keys', JSON.stringify(addKeys));

      const response = await fetch(`${API_URL}/api/onboarding/token/${token}/upload`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit documents');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const FileUploadField = ({ field, label, required, accept = ".pdf,.jpg,.jpeg,.png" }) => (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors" data-testid={`upload-field-${field}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className={`w-5 h-5 ${files[field] ? 'text-green-600' : 'text-gray-400'}`} />
          <div>
            <p className="font-medium text-gray-900">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </p>
            {files[field] && (
              <p className="text-sm text-green-600" data-testid={`file-name-${field}`}>{files[field].name}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {files[field] && (
            <button
              type="button"
              onClick={() => handleFileChange(field, null)}
              data-testid={`remove-file-${field}`}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <label className="cursor-pointer px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">
            {files[field] ? 'Replace' : 'Choose File'}
            <input
              type="file"
              accept={accept}
              data-testid={`file-input-${field}`}
              onChange={(e) => handleFileChange(field, e.target.files[0])}
              className="hidden"
            />
          </label>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="driver-onboarding-page">
        <PublicNavbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-900 mx-auto mb-4" />
          <p className="text-gray-600">Validating your onboarding link...</p>
        </div>
        <PublicFooter />
      </div>
    );
  }

  // Invalid token
  if (error && !tokenData) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="driver-onboarding-page">
        <PublicNavbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid or Expired Link</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <p className="text-gray-500 mb-8">
            If you believe this is an error, please contact us at{' '}
            <a href="mailto:drivers@bluelockfreights.com" className="text-blue-900 hover:underline">
              drivers@bluelockfreights.com
            </a>
          </p>
          <Link
            to="/"
            className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Home
          </Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="driver-onboarding-page">
        <PublicNavbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4" data-testid="success-heading">
            Documents Received
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            Your onboarding package is under review. You will be contacted with next steps.
          </p>
          <p className="text-gray-500 mb-8">
            If additional documentation is required, a team member will contact you. Review time varies (typically 1–2 business days).
          </p>
          <div className="bg-blue-50 rounded-xl p-6 max-w-md mx-auto">
            <p className="text-sm text-blue-800">
              Questions? Contact us at{' '}
              <a href="mailto:drivers@bluelockfreights.com" className="font-medium hover:underline">
                drivers@bluelockfreights.com
              </a>
            </p>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gray-50" data-testid="driver-onboarding-page">
      <PublicNavbar />

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-blue-900" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Driver Onboarding
          </h1>
          <p className="text-gray-600">
            Welcome{tokenData?.first_name ? `, ${tokenData.first_name}` : ''}! Please upload your documents to complete your onboarding.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="onboarding-form-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Contact Info */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-400" />
              Confirm Your Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="input-name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      data-testid="input-email"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      data-testid="input-phone"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Authoritative role-aware requirements */}
          {(() => {
            const v = getReqView(tokenData);
            if (!v.hasReqs) return null;
            return (
              <div className="mb-8" data-testid="applicant-requirements">
                <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  What Applies To You
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Based on your application. "Required" items are needed now; "Verify" items may be
                  confirmed with you; "Conditional" items apply only if a specific fact is true.
                </p>
                <ul className="space-y-2" data-testid="applicant-requirements-list">
                  {v.reqs
                    .filter((r) => r.status !== 'NOT_APPLICABLE' && r.status !== 'NOT_REQUIRED')
                    .map((r) => (
                      <li
                        key={r.key}
                        className="flex items-start justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2"
                        data-testid={`applicant-req-${r.key}`}
                      >
                        <span className="text-sm text-gray-800">{r.label}</span>
                        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${REQ_TONE[r.status] || REQ_TONE.NOT_REQUIRED}`}>
                          {REQ_LABEL[r.status] || r.status}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            );
          })()}

          {/* Document Uploads */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-gray-400" />
              Upload Documents
            </h2>
            <p className="text-sm text-gray-500 mb-2">
              Accepted formats: PDF, JPG, PNG (max 10MB each)
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> Do not upload sensitive personal information unrelated to onboarding (e.g., Social Security card). Only requested documents are required.
              </p>
            </div>
            {(() => {
              const v = getReqView(tokenData);
              return (
                <div className="space-y-3">
                  {v.showCdl && <FileUploadField field="cdl" label="CDL (Commercial Driver's License)" required />}
                  {v.showMedical && <FileUploadField field="medical_card" label="Medical Card" required />}
                  {v.showW9 && <FileUploadField field="w9" label="W-9 Form" required />}
                  {v.showInsurance && <FileUploadField field="insurance" label="Certificate of Insurance" required />}
                  {v.otherRequired.map((r) => (
                    <FileUploadField key={r.key} field={r.key} label={r.label} required />
                  ))}
                  <FileUploadField field="other_doc" label="Additional Document (optional)" required={false} />
                </div>
              );
            })()}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-onboarding-btn"
            className="w-full px-6 py-3 bg-blue-900 text-white rounded-lg font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Submit Onboarding Package
              </>
            )}
          </button>
        </form>

        {/* Help */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Need help? Contact{' '}
            <a href="mailto:drivers@bluelockfreights.com" className="text-blue-900 hover:underline">
              drivers@bluelockfreights.com
            </a>
          </p>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default DriverOnboarding;
